import AppContext from 'App/AppContext';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { AdaptiveSubmit, JsonObject } from 'components/AdaptiveCard/AdaptiveCardRenderer';
import { getEvidenceAuditRefusalText } from 'components/AuditCriteria/evidenceAuditTexts';
import { getErrorMessageText } from 'components/Notification/ErrorMessageService';
import { globalStackTokensGapSmall } from 'globalStyles';
import { mapFromCopilotContextProposalPayload } from 'mappings/copilotMapping';
import { CopilotAction, CopilotActionType } from 'models/copilot/copilotAction';
import { CopilotContextProposalItem } from 'models/copilot/copilotContextProposal';
import {
  ContentBlockKind,
  CopilotConversation,
  CopilotConversationContentBlock,
  CopilotConversationItem,
  CopilotConversionItemType,
} from 'models/copilot/copilotConversation';
import { CopilotGenericContent } from 'models/copilot/copilotGeneric';
import { EntityTypes } from 'models/entity';
import Task from 'models/tasks/task';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiRunAssessmentEvidenceAudit } from 'services/Api/auditCriteriaService';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';
import { CopilotAgentChat } from '../Agent/CopilotAgentChat';
import { CopilotAgentHeader } from '../Agent/CopilotAgentHeader';
import { useCopilotAgentSession } from '../Agent/useCopilotAgentSession';
import { CopilotActionButtons } from '../Panel/Conversation/CopilotActionButtons';
import {
  TaskAssistantAction,
  TaskAssistantCheckSkill,
  TaskAssistantContextKey,
  TaskAssistantContextSkill,
} from './taskAssistant';

interface ITaskAssistantPaneProps {
  task: Task;
  // Why the pane was opened: 'evidence' is the criterion path, which opens the conversation itself; any other
  // action is the AI button, which opens the pane empty with the task's actions as buttons above the chat.
  action: TaskAssistantAction;
  // The user may edit the task's Context: only then is "Suggest relevant context" offered.
  canSuggestContext?: boolean;
  // A workable task of a tenant with the preview copilot.audit: only then is "Check evidence" offered.
  canCheckEvidence?: boolean;
  // An open assessment task of a tenant with the Audit preview: only then is "Audit the evidence with AI" offered.
  canAuditEvidence?: boolean;
  // Raised by the host after an upload of evidence: each new value runs "Check evidence" once.
  checkRequest?: number;
  // The task holds no evidence yet (no description, checklist or documents). In the evidence action
  // the agent then opens the conversation itself with a proposal instead of waiting for the user.
  isTaskEmpty: boolean;
  // Runs before every message: the host saves the task when it has unsaved changes, so the agent reads
  // and writes the stored task the user is looking at. False aborts the message (the save was refused).
  beforeSend: () => Promise<boolean>;
  // Runs after every completed turn: the agent may have written into the stored task, so the host reloads it.
  // Not after a context turn: it writes nothing, and a reload would drop the context it applied.
  onTurnCompleted: () => Promise<void> | void;
  // The context action's proposal, once per ContextProposal block: the host adds the items to its unsaved task and
  // returns how many were new.
  onContextProposal?: (items: CopilotContextProposalItem[]) => number;
  // Closes the pane; absent when the host keeps it open for the life of the modal (the criterion path).
  onClose?: () => void;
}

// the skill a turn of the action names: the context and the check run with their own; explain and evidence with none
function skillOf(turnAction: TaskAssistantAction): string | undefined {
  switch (turnAction) {
    case 'context':
      return TaskAssistantContextSkill;
    case 'check':
      return TaskAssistantCheckSkill;
    default:
      return undefined;
  }
}

/**
 * The AI assistant beside a task: an embedded Copilot session (the same host the document editor uses)
 * posting to the general completion with the TaskAssistant context key and the task as its entity, so
 * the server runs the TaskAgent's conversation loop. The server picks the agent's mode from the task:
 * a task bound to one-time audit criteria gets the evidence assistant (reads, gathers facts, writes its
 * proposal into the task and marks what the user must confirm); any other task gets the read-only assistant that
 * explains the task and its context. Opened from the AI button, the pane lists the task's actions as the main panel
 * lists a scene's: Explain this task, and Suggest relevant context, which runs that assistant with the
 * task-context-suggest skill and proposes the relevant context in a ContextProposal block that the host adds to its
 * unsaved task, and Check evidence (a preview feature), which judges the task's stored evidence without the model loop
 * and answers with the evidence status table; the host also runs it after an upload of evidence. On an open assessment
 * task, Audit the evidence with AI starts the item's AI evidence audit (PLAN-CTI-090) and says in the pane whether it
 * started; it is no turn of the agent. A clicked action stays
 * active for the questions after it, in the same conversation; a question after a check is answered by the explain
 * loop. This pane only keeps the modal and the stored task in step around each turn.
 */
export const TaskAssistantPane = (props: ITaskAssistantPaneProps) => {
  const { t } = useTranslation(['copilot', 'task', 'criteria']);
  const appContext = useContext(AppContext);
  const taskId = props.task.taskId;
  // the action the user clicked last; until then the pane runs the action it was opened with
  const [chosenAction, setChosenAction] = useState<TaskAssistantAction | undefined>(undefined);
  const action = chosenAction ?? props.action;

  // The entity the conversation is about; the generic definition maps it to the entity ref of every
  // completion. Memoized on the id: a new content object per render would re-key the runtime.
  const content = useMemo(() => {
    const output = new CopilotGenericContent();
    output.contextKey = TaskAssistantContextKey;
    output.entityType = EntityTypes.Task;
    output.entityId = taskId;

    return output;
  }, [taskId]);

  const session = useCopilotAgentSession({ contextKey: TaskAssistantContextKey, content });
  const {
    conversation,
    setConversation,
    copilotSessionId,
    streamState,
    isStreaming,
    isLoading,
    onNewQuestion,
    onEditLastQuestion,
    onEntitySelection,
    onAdaptiveInputsChanged,
    onAdaptiveSubmit,
    onApprovalDetails,
    onStop,
  } = session;

  // Every way a turn can start goes through here: save first, run, then hand the host the task the
  // agent may have written into. A refused save (the host's form validation) is said out loud, or
  // the user would wonder why nothing was sent. The turn's action is passed in, because a click on an
  // action sets it and sends in the same event.
  const runTurn = useCallback(
    async (turnAction: TaskAssistantAction, turn: () => Promise<void> | void): Promise<void> => {
      const canSend = await props.beforeSend();
      if (!canSend) {
        appContext.showNotification(t('copilot:TaskAssistant.SaveFailed'), true, false, true);

        return;
      }

      await turn();
      if (turnAction !== 'context') await props.onTurnCompleted();
    },
    [props, appContext, t],
  );

  // the context action names its skill on every question, so a follow-up question keeps the context tools; the check
  // names its skill only when it is run, so a question after a check is answered by the explain loop
  const ask = useCallback(
    (question: string, turnAction: TaskAssistantAction) =>
      runTurn(turnAction, () => onNewQuestion(question, undefined, skillOf(turnAction))),
    [runTurn, onNewQuestion],
  );

  const sendQuestion = useCallback(
    (question: string) => ask(question, action === 'check' ? 'explain' : action),
    [ask, action],
  );

  const editLastQuestion = useCallback(
    (itemId: string | undefined, question: string) => runTurn(action, () => onEditLastQuestion(itemId, question)),
    [runTurn, onEditLastQuestion, action],
  );

  const submitAdaptive = useCallback(
    (itemId: string | undefined, block: CopilotConversationContentBlock, payload: AdaptiveSubmit<JsonObject>) =>
      runTurn(action, () => onAdaptiveSubmit(itemId, block, payload)),
    [runTurn, onAdaptiveSubmit, action],
  );

  // The task's actions, drawn by the main panel's action buttons: none on the criterion path, which is about
  // evidence only; the context action only for a user who may edit the task's Context.
  const actions = useMemo((): CopilotAction[] => {
    if (props.action === 'evidence') return [];

    const toAction = (id: TaskAssistantAction, name: string, prompt: string, skill?: string): CopilotAction => {
      const output = new CopilotAction();
      output.id = id;
      output.name = name;
      output.type = CopilotActionType.Prompt;
      output.prompt = prompt;
      output.skill = skill;
      output.selected = chosenAction === id;

      return output;
    };

    const output = [toAction('explain', t('task:TopTasks.ExplainTask'), t('copilot:TaskAssistant.ExplainQuestion'))];
    // an assessment task: the agent answers the form's questions (PLAN-075), first; the person checks and completes
    if (props.action === 'assessment') {
      output.unshift(
        toAction('assessment', t('task:TopTasks.AnswerAssessment'), t('copilot:TaskAssistant.AssessmentQuestion')),
      );
    }
    if (props.canSuggestContext) {
      output.push(
        toAction(
          'context',
          t('task:TopTasks.SuggestContext'),
          t('copilot:TaskAssistant.ContextQuestion'),
          TaskAssistantContextSkill,
        ),
      );
    }

    // the AI evidence audit of the item (PLAN-CTI-090): no question to the agent, a start of the audit for this task
    if (props.action === 'assessment' && props.canAuditEvidence) {
      output.splice(1, 0, toAction('audit', t('task:TopTasks.AuditEvidence'), ''));
    }

    if (props.canCheckEvidence) {
      output.push(
        toAction(
          'check',
          t('task:TopTasks.CheckEvidence'),
          t('copilot:TaskAssistant.CheckQuestion'),
          TaskAssistantCheckSkill,
        ),
      );
    }

    return output;
  }, [props.action, props.canSuggestContext, props.canCheckEvidence, props.canAuditEvidence, chosenAction, t]);

  // The audit's answer as one line of the pane's conversation, which stays in the pane only, and as the bottom toast.
  const tellAuditResult = (message: string, isWarning: boolean) => {
    const item = new CopilotConversationItem();
    item.type = CopilotConversionItemType.Copilot;
    item.response = message;
    setConversation((current) => {
      const next = current ? current.clone() : new CopilotConversation();
      next.thread.push(item);

      return next;
    });
    appContext.showNotification(message, false, isWarning, true);
  };

  // "Audit the evidence with AI": the server starts the item's evidence audit in the background with this task as its
  // consumer. The pane does not follow the run; its answers are on the form after a reload, which the started text says.
  // A refusal the server explains is the pane's answer.
  const [isAuditing, setIsAuditing] = useState<boolean>(false);
  const runEvidenceAudit = async () => {
    try {
      setIsAuditing(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const audit = (await apiRunAssessmentEvidenceAudit(taskId, accessToken)).evidenceAudit;
      if (audit?.started) {
        tellAuditResult(t('criteria:Generic.Start.EvidenceAuditStartedFromTask'), false);
      } else {
        const reason = getEvidenceAuditRefusalText(audit?.reason, t);
        tellAuditResult(t('criteria:Generic.Start.EvidenceAuditRefused', { reason: reason }), true);
      }
    } catch (err) {
      if (err instanceof AppError && AppError.isControllerException(err)) {
        tellAuditResult(getErrorMessageText(err, t), true);
      } else {
        appContext.setError(err);
      }
    } finally {
      setIsAuditing(false);
    }
  };

  const runAction = (item: CopilotAction) => {
    const clicked = item.id as TaskAssistantAction;
    // the audit is no turn: the action the pane runs stays the one before it
    if (clicked === 'audit') {
      void runEvidenceAudit();

      return;
    }

    setChosenAction(clicked);
    void ask(item.prompt ?? '', clicked);
  };

  // A ContextProposal block of an answer is applied to the open task once, when it arrives; the card in the chat
  // stays the record of what was added and why.
  const { onContextProposal } = props;
  const appliedProposals = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!onContextProposal || !conversation) return;

    conversation.thread.forEach((item, itemIndex) => {
      (item.contentBlocks ?? []).forEach((block, blockIndex) => {
        const key = block.id ?? `${item.id ?? itemIndex}-${blockIndex}`;
        if (block.kind !== ContentBlockKind.ContextProposal || appliedProposals.current.has(key)) return;

        appliedProposals.current.add(key);
        const proposal = mapFromCopilotContextProposalPayload(block.payload);
        if (!proposal || proposal.items.length === 0) return;

        const added = onContextProposal(proposal.items);
        if (added > 0) {
          const message = t('copilot:TaskAssistant.ContextApplied', { count: added });
          appContext.showNotification(message, false, false, true);
        }
      });
    });
  }, [conversation, onContextProposal, appContext, t]);

  // The opening move of the criterion path, sent once when the conversation is still empty and only for an empty
  // task, so the first thing the user sees is a proposal or the agent's first question; a task with content waits
  // for the user. The AI button opens the pane empty: the user picks an action or types.
  const openingQuestion =
    props.action === 'evidence' && props.isTaskEmpty ? t('copilot:TaskAssistant.EvidenceKickoff') : undefined;
  const kickedOff = useRef<boolean>(false);
  useEffect(() => {
    if (kickedOff.current || !openingQuestion || conversation) return;

    kickedOff.current = true;
    void sendQuestion(openingQuestion);
  }, [openingQuestion, conversation, sendQuestion]);

  // The host's request to check after an upload: each new value runs the check once, as a click on its chip would.
  // The pane opened by the upload starts with the check's welcome; one already open switches to the check.
  const { checkRequest, canCheckEvidence } = props;
  const handledCheckRequest = useRef<number>(0);
  useEffect(() => {
    if (!canCheckEvidence || !checkRequest || checkRequest === handledCheckRequest.current) return;

    handledCheckRequest.current = checkRequest;
    setChosenAction('check');
    void ask(t('copilot:TaskAssistant.CheckQuestion'), 'check');
  }, [checkRequest, canCheckEvidence, ask, t]);

  const getWelcome = (): string => {
    switch (action) {
      case 'explain':
        return t('copilot:TaskAssistant.ExplainWelcome');
      case 'context':
        return t('copilot:TaskAssistant.ContextWelcome');
      case 'assessment':
        return t('copilot:TaskAssistant.AssessmentWelcome');
      case 'check':
        return t('copilot:TaskAssistant.CheckWelcome');
      default:
        return t('copilot:TaskAssistant.EvidenceWelcome');
    }
  };

  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall} styles={{ root: { minHeight: 0, height: '100%' } }}>
      <IPStack.Item>
        <CopilotAgentHeader onClose={props.onClose} />
      </IPStack.Item>
      {actions.length > 0 && (
        <IPStack.Item>
          <CopilotActionButtons
            actions={actions}
            compact
            disabled={isStreaming || isLoading || isAuditing}
            onActionClick={runAction}
          />
        </IPStack.Item>
      )}
      <IPStack.Item grow styles={{ root: { minHeight: 0, display: 'flex' } }}>
        <CopilotAgentChat
          conversation={conversation}
          sessionId={copilotSessionId}
          isStreaming={isStreaming}
          isLoading={isLoading}
          // lock the input while the agent works, so the user cannot queue a question mid-run
          chatDisabled={isStreaming || isLoading}
          streamState={streamState}
          welcomeMessage={getWelcome()}
          onNewQuestion={sendQuestion}
          onStop={onStop}
          onEntitySelection={onEntitySelection}
          onAdaptiveInputsChanged={onAdaptiveInputsChanged}
          onAdaptiveSubmit={submitAdaptive}
          onApprovalDetails={onApprovalDetails}
          //an edited question is sent without a skill, so the context action, which needs its skill on every turn, offers no edit
          onEditLastQuestion={action === 'context' ? undefined : editLastQuestion}
        />
      </IPStack.Item>
    </IPStack>
  );
};

export default TaskAssistantPane;
